import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MeetingRerunRequest } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

/**
 * TM-fe04aa2b — «Переанализировать» dialog.
 *
 * Opens with the textarea pre-filled from the latest version's
 * `prompt_text` (the active prompt of the meeting type at the time of
 * the last run). The operator can edit and submit, sending the new
 * text as `custom_prompt_text`; or keep it unchanged and submit with
 * no body so the BE attaches the normal `prompt_version_id` and the
 * Versions tab keeps showing the run linked to the type's prompt.
 */
export interface RerunDialogProps {
  open: boolean;
  /** Pre-fill source — the active prompt at the time of the last run. */
  initialPromptText: string;
  isPending: boolean;
  onSubmit: (payload?: MeetingRerunRequest) => void;
  onClose: () => void;
}

const MAX_LEN = 50000;

export function RerunDialog({
  open,
  initialPromptText,
  isPending,
  onSubmit,
  onClose,
}: RerunDialogProps) {
  const { t } = useTranslation();
  const baseline = useMemo(() => initialPromptText ?? "", [initialPromptText]);
  const [value, setValue] = useState(baseline);

  // Re-seed when the dialog re-opens with a different baseline.
  useEffect(() => {
    if (open) setValue(baseline);
  }, [open, baseline]);

  const handleSubmit = () => {
    // Unchanged ⇒ POST with no body so the BE attaches the normal
    // `prompt_version_id` and the Versions tab keeps the «промпт N»
    // disclosure rather than badging the row «свой промпт».
    if (value === baseline) {
      onSubmit();
      return;
    }
    onSubmit({ custom_prompt_text: value });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("meetingCard.rerun.dialog.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setValue(baseline)}
            data-testid="rerun-dialog-use-active"
          >
            {t("meetingCard.rerun.dialog.useActive")}
          </Button>
          <div>
            <Label htmlFor="rerun-prompt">
              {t("meetingCard.rerun.dialog.promptLabel")}
            </Label>
            <textarea
              id="rerun-prompt"
              className="min-h-48 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              maxLength={MAX_LEN}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid="rerun-dialog-textarea"
            />
            <div className="mt-1 text-right text-xs text-muted-foreground">
              {value.length}/{MAX_LEN}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="rerun-dialog-cancel"
          >
            {t("meetingCard.rerun.dialog.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isPending || value.length > MAX_LEN}
            data-testid="rerun-dialog-submit"
          >
            {t("meetingCard.rerun.dialog.submit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
